import React, {
  createContext,
  useContext,
  useRef,
  useCallback,
  RefObject,
} from "react";

export type SectionEntry = {
  id: string;
  title?: string;
  ref: RefObject<HTMLElement>;
};

type ScreenshotContextType = {
  registerSection: (entry: SectionEntry) => void;
  unregisterSection: (id: string) => void;
  getSections: () => SectionEntry[]; // ordered
};

const ScreenshotContext = createContext<ScreenshotContextType | null>(null);

export const ScreenshotProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // keep a stable, ordered list across renders
  const sectionsRef = useRef<SectionEntry[]>([]);

  const registerSection = useCallback((entry: SectionEntry) => {
    const list = sectionsRef.current;
    const idx = list.findIndex(s => s.id === entry.id);
    if (idx >= 0) {
      list[idx] = entry; // update
    } else {
      list.push(entry);  // preserve order of registration
    }
  }, []);

  const unregisterSection = useCallback((id: string) => {
    sectionsRef.current = sectionsRef.current.filter(s => s.id !== id);
  }, []);

  const getSections = useCallback(() => sectionsRef.current.slice(), []);

  return (
    <ScreenshotContext.Provider value={{ registerSection, unregisterSection, getSections }}>
      {children}
    </ScreenshotContext.Provider>
  );
};

export const useScreenshotContext = () => {
  const ctx = useContext(ScreenshotContext);
  if (!ctx) throw new Error("useScreenshotContext must be used within <ScreenshotProvider>");
  return ctx;
};
